import {
	AnalyticsCardsContainer,
	EnrollmentsCard,
	EnrollmentTypeCard,
	TypeInfo,
	TypeName,
	TypeValue,
} from '@Components/UI/Analytics/Analytics.styled';
import { AnalyticsTotal } from '@Components/UI/Analytics/Assets/Icons/Icons';
import { DataCard, DataCardLabel, DataCardValue } from '@Components/UI/Cards/DataCard/DataCard';
import { Palette } from '@mui/material';
import { egp } from '@ContentManagement/EnrollmentInvoices';
import React from 'react';
import styled from 'styled-components';
import {
	HUB_METHODS,
	HUB_METHOD_LABELS,
	hubMethodIcons,
	pickMethodColor,
} from '../Constants/methods';
import { MethodBucketList, RevenueSummary } from '../Types/Analytics.types';

/** KPI cards row — the classroom/standalone Analytics card row, 6 buckets. */
export const EnrollmentSummaryCards: React.FC<{
	summary?: MethodBucketList | null;
	totalLabel?: string;
	viaBundle?: number;
}> = ({ summary, totalLabel = 'Enrollments', viaBundle }) => {
	if (!summary) return null;
	return (
		<ResponsiveCards>
			<EnrollmentsCard elevation={10}>
				<AnalyticsTotal />
				<TypeInfo>
					<span>{summary.purchase_methods_total}</span>
					<label>{totalLabel}</label>
				</TypeInfo>
			</EnrollmentsCard>
			{summary.purchase_methods.map((method) => (
				<EnrollmentTypeCard
					key={method.purchase_method}
					color={pickMethodColor(method.purchase_method)}
				>
					<TypeInfo>
						{hubMethodIcons[method.purchase_method as keyof typeof hubMethodIcons] ??
							hubMethodIcons.free}
						<TypeValue>{method.purchase_method_count}</TypeValue>
					</TypeInfo>
					<TypeName>{method.purchase_method}</TypeName>
				</EnrollmentTypeCard>
			))}
			{viaBundle !== undefined && (
				<EnrollmentTypeCard color="#607d8b">
					<TypeInfo>
						{hubMethodIcons.instructor}
						<TypeValue>{viaBundle}</TypeValue>
					</TypeInfo>
					<TypeName>via bundle</TypeName>
				</EnrollmentTypeCard>
			)}
		</ResponsiveCards>
	);
};

/**
 * Money cards row — EnrollmentInvoices summary style: X EGP + (Y enrollments)
 * per method. `showRefundCards` off when Refunds/Total Revenue live in a
 * separate row on the page (Overview) so they are not shown twice.
 */
export const RevenueSummaryCards: React.FC<{
	summary?: RevenueSummary | null;
	showRefundCards?: boolean;
	/** Small line under the total, e.g. "value of active enrollments". */
	totalSubLabel?: string;
}> = ({ summary, showRefundCards = true, totalSubLabel }) => {
	if (!summary) return null;
	return (
		<ResponsiveCards>
			<EnrollmentsCard elevation={10}>
				<AnalyticsTotal />
				<TypeInfo>
					<span>{egp(summary.grand_total)}</span>
					<label>{`Total Sales (${summary.enrollments} enrollments)`}</label>
					{totalSubLabel && <SubNote>{totalSubLabel}</SubNote>}
				</TypeInfo>
			</EnrollmentsCard>
			{HUB_METHODS.map((method) => (
				<EnrollmentTypeCard key={method} color={pickMethodColor(method)}>
					<TypeInfo>
						{hubMethodIcons[method]}
						<TypeValue>{egp(summary.methods?.[method]?.total)}</TypeValue>
					</TypeInfo>
					<TypeName>{`${HUB_METHOD_LABELS[method]} (${
						summary.methods?.[method]?.count ?? 0
					} enrolls)`}</TypeName>
				</EnrollmentTypeCard>
			))}
			{showRefundCards && (
				<>
					<EnrollmentTypeCard color="#ff3100">
						<TypeInfo>
							{hubMethodIcons.admin}
							<TypeValue>{egp(summary.refunded)}</TypeValue>
						</TypeInfo>
						<TypeName>{`Refunded (${summary.refund_count})`}</TypeName>
					</EnrollmentTypeCard>
					<EnrollmentTypeCard color="#00d66b">
						<TypeInfo>
							{hubMethodIcons.wallet}
							<TypeValue>{egp(summary.net)}</TypeValue>
						</TypeInfo>
						<TypeName>Total Revenue</TypeName>
					</EnrollmentTypeCard>
				</>
			)}
		</ResponsiveCards>
	);
};

/** Simple labeled money tiles (Overview revenue & wallet rows). */
export const DataCardsRow: React.FC<{
	items: Array<{ label: string; value: string; sub?: string; color?: keyof Palette }>;
}> = ({ items }) => (
	<CardsGrid>
		{items.map((item) => (
			<DataCard key={item.label} color={item.color ?? 'primary'} flexDirection="column">
				<DataCardLabel>{item.label}</DataCardLabel>
				<DataCardValue>{item.value}</DataCardValue>
				{item.sub && <SubNote>{item.sub}</SubNote>}
			</DataCard>
		))}
	</CardsGrid>
);

const ResponsiveCards = styled(AnalyticsCardsContainer)`
	width: 100%;
	flex-wrap: wrap;
	gap: 1rem;
	margin-bottom: 2rem;

	> * {
		margin: 0;
	}
`;

const CardsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
	gap: 1rem;
	width: 100%;
	margin-bottom: 2rem;
`;

const SubNote = styled.span`
	font-size: 0.75rem;
	color: rgba(0, 0, 0, 0.55);
`;
